import { defineStore, storeToRefs } from 'pinia'
import { ref, computed, watch } from 'vue'
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore'
import { firestore } from '@/firebase'
import {
  firestoreStripeProductSchema,
  isProductAvailableOnSite,
  STRIPE_PRODUCT_METADATA_CONSULT_TYPE,
  type ClinicalConsultTypeProduct,
  type ConsultChargeKindProduct,
  type FirestoreStripeProduct,
} from '@gh/common/models'
import { useAuthStore } from './auth'
import { useStripeCouponsStore } from './stripeCoupons'
import type { StripeCoupon } from '@gh/common/models'
import createLogger from '@/utils/logger'
import type { StripeProductId } from '@gh/common/models'

const logger = createLogger('stores/products.ts')

export const useProductsStore = defineStore('products', () => {
  const products = ref<FirestoreStripeProduct[]>([])
  const isLoading = ref(true)
  const error = ref<string | null>(null)
  let unsubscribeProductsListener: (() => void) | null = null

  const authStore = useAuthStore()
  const authUser = computed(() => authStore.user)
  const stripeCouponsStore = useStripeCouponsStore()
  const stripeCoupon = ref<StripeCoupon | null>(null)
  const stripeCouponLoading = ref(true)
  const { promoCode, isLoading: isLoadingStripeCouponsStore } = storeToRefs(stripeCouponsStore)

  // Set up real-time listener for products
  const setupProductsListener = () => {
    const unsubscribe = onSnapshot(
      collection(firestore, 'products'),
      (snapshot) => {
        const productsData: FirestoreStripeProduct[] = []

        snapshot.forEach((doc) => {
          const { success, data, error } = firestoreStripeProductSchema.safeParse({
            id: doc.id,
            ...doc.data(),
          })
          if (!success) {
            logger.error('Error parsing product data', { data, error, snap: doc.data() })
            return
          }
          productsData.push(data)
        })

        products.value = productsData
        isLoading.value = false
      },
      (err) => {
        logger.error('Error listening to products:', err)
        error.value = 'Failed to listen to products updates.'
        isLoading.value = false
      },
    )

    return unsubscribe
  }

  // Computed properties
  const activeProducts = computed(() => products.value.filter((product) => product.active))

  const getProductById = computed(
    () => (stripeProductId: StripeProductId) =>
      products.value.find((product) => product.stripeProductId === stripeProductId),
  )

  const getConsultChargeProduct = computed(
    () =>
      (kind: ConsultChargeKindProduct): { name: string; amountCents: number } | null => {
        const product = activeProducts.value.find((row) => row.metadata?.consultChargeKind === kind)
        if (
          !product?.price?.defaultPriceId ||
          !product.price?.amount ||
          product.price?.amount <= 0
        ) {
          return null
        }
        return { name: product.name, amountCents: product.price.amount }
      },
  )

  const getConsultTypeProduct = computed(
    () =>
      (consultType: ClinicalConsultTypeProduct): { name: string; amountCents: number } | null => {
        const product = activeProducts.value.find(
          (row) => row.metadata?.[STRIPE_PRODUCT_METADATA_CONSULT_TYPE] === consultType,
        )
        if (
          !product?.price?.defaultPriceId ||
          !product.price?.amount ||
          product.price?.amount <= 0
        ) {
          return null
        }
        return { name: product.name, amountCents: product.price.amount }
      },
  )

  const formatPrice = (product: { price: { amount: number; currency: string } }) => {
    if (!product.price.amount || !product.price.currency) {
      return { price: 'No price', unit: 'month' }
    }
    const amount = product.price.amount / 100.0
    const locale = product.price.currency === 'usd' ? 'en-US' : 'de-DE'
    const formattedPrice = new Intl.NumberFormat(locale, {
      style: 'currency',
      minimumFractionDigits: 0,
      currency: product.price.currency.toUpperCase(),
    }).format(amount)
    return { price: formattedPrice, unit: 'month' }
  }

  const couponDataCache = ref<Record<string, StripeCoupon | null>>({})

  // Fetch coupon data from Firestore
  const fetchCouponDataRaw = async (promotionCode: string): Promise<StripeCoupon | null> => {
    try {
      // Query coupons by promotion code using array-contains
      // This will only return documents that contain the promotion code
      const couponsQuery = query(
        collection(firestore, 'stripe_coupons'),
        where(`promotionCodes.${promotionCode}`, '!=', null),
      )

      const couponsSnapshot = await getDocs(couponsQuery)

      for (const doc of couponsSnapshot.docs) {
        const data = doc.data() as {
          coupon: StripeCoupon
          promotionCodes: Record<
            string,
            { code: string; id: string; coupon: string; active: boolean }
          >
        }

        // Check if the promotion code exists in the promotionCodes object
        const matchingPromoCode = data.promotionCodes?.[promotionCode]
        if (matchingPromoCode) {
          const coupon = data.coupon

          if (!matchingPromoCode.active) {
            logger.warn(`Promotion code ${promotionCode} is not active`)
            continue
          }

          // Validate coupon
          if (!coupon.valid) {
            logger.warn(`Coupon ${coupon.name} is not valid`)
            continue
          }

          if (coupon.max_redemptions && coupon.times_redeemed >= coupon.max_redemptions) {
            logger.warn(`Coupon ${coupon.name} has reached maximum redemptions`)
            continue
          }

          return coupon
        }
      }

      logger.warn(`Promotion code ${promotionCode} not found`)
      return null
    } catch (error) {
      logger.error('Error fetching coupon data:', error)
      return null
    }
  }

  const fetchCouponData = async (promotionCode: string): Promise<StripeCoupon | null> => {
    if (couponDataCache.value[promotionCode] !== undefined) {
      return couponDataCache.value[promotionCode]
    }
    const coupon = await fetchCouponDataRaw(promotionCode)
    couponDataCache.value[promotionCode] = coupon
    return coupon
  }

  // Calculate discounted price
  const calculateDiscountedPrice = (product: FirestoreStripeProduct, coupon: StripeCoupon) => {
    if (!product.price.amount || !product.price.currency) {
      return null
    }

    const originalAmount = product.price.amount
    let discountedAmount: number

    if (coupon.percent_off) {
      // Percentage discount
      discountedAmount = originalAmount * (1 - coupon.percent_off / 100)
    } else if (coupon.amount_off) {
      // Fixed amount discount
      discountedAmount = Math.max(0, originalAmount - coupon.amount_off)
    } else {
      return null
    }
    return Math.round(discountedAmount)
  }

  const productsWithPromoApplied = computed(() => {
    if (stripeCouponLoading.value) {
      return []
    }
    return activeProducts.value
      .map((product) => {
        const pricingData = formatPriceWithCoupon(product, stripeCoupon.value)
        return {
          ...product,
          pricingData: pricingData,
        }
      })
      .sort((a, b) => b.price.amount - a.price.amount)
  })

  const activeProductsAvailableOnSite = computed(() =>
    activeProducts.value.filter(isProductAvailableOnSite),
  )

  const productsAvailableOnSiteWithPromo = computed(() =>
    productsWithPromoApplied.value.filter(isProductAvailableOnSite),
  )

  const couponAppliesToStripeProduct = (stripeProductId: StripeProductId) => {
    const coupon = stripeCoupon.value
    if (!coupon) {
      return false
    }
    const listOfProductsAppliesTo = coupon.applies_to?.products || []
    if (listOfProductsAppliesTo.length === 0) {
      return true
    }
    return listOfProductsAppliesTo.includes(stripeProductId)
  }

  // Format price with coupon applied
  const formatPriceWithCoupon = (product: FirestoreStripeProduct, coupon: StripeCoupon | null) => {
    const promotionCode = promoCode.value
    const originalPriceData = formatPrice(product)
    if (!promotionCode || !coupon || !couponAppliesToStripeProduct(product.stripeProductId)) {
      return {
        originalPrice: originalPriceData.price,
        originalUnit: originalPriceData.unit,
        discountedPrice: null,
        discount: null,
      }
    }

    const discountedAmount = calculateDiscountedPrice(product, coupon)
    if (discountedAmount == null) {
      return {
        originalPrice: originalPriceData.price,
        originalUnit: originalPriceData.unit,
        discountedPrice: null,
        discount: null,
      }
    }

    const formattedDiscountedPrice = formatPrice({
      price: {
        amount: discountedAmount,
        currency: product.price.currency,
      },
    }).price

    // Calculate discount percentage for display
    const originalAmount = product.price.amount
    const discountPercentage = Math.round(
      ((originalAmount - discountedAmount) / originalAmount) * 100,
    )

    let text: string | null = null

    if (coupon.duration === 'once') {
      text = `Promo Applied! ${discountPercentage}% off your first month`
    } else if (coupon.duration === 'repeating') {
      if (coupon.duration_in_months === 1) {
        // if duration is 1 month, then it's the first month
        text = `Promo Applied! ${discountPercentage}% off your first month`
      } else {
        // if duration is more than 1 month, then it's the ${coupon.duration_in_months} months
        text = `Promo Applied! ${discountPercentage}% off your first ${coupon.duration_in_months} months`
      }
    } else if (coupon.duration === 'forever') {
      text = `Promo Applied! ${discountPercentage}% off, forever`
    } else {
      text = `Promo Applied! ${discountPercentage}% off.`
    }

    return {
      originalPrice: originalPriceData.price,
      originalUnit: originalPriceData.unit,
      discountedPrice: formattedDiscountedPrice,
      discount: {
        promoCode: promotionCode,
        text: text || `Promo Applied! ${discountPercentage}% off.`,
      },
    }
  }

  function signOut() {
    if (unsubscribeProductsListener) {
      unsubscribeProductsListener()
    }
    products.value = []
  }

  function reloadProductsCatalog(): void {
    error.value = null
    isLoading.value = true
    if (unsubscribeProductsListener) {
      unsubscribeProductsListener()
      unsubscribeProductsListener = null
    }
    if (authUser.value) {
      unsubscribeProductsListener = setupProductsListener()
    } else {
      isLoading.value = false
    }
  }

  watch(
    [promoCode, isLoadingStripeCouponsStore],
    async (newVal) => {
      const promoCodeNew = newVal[0]
      const isLoadingStripeCouponsStoreNew = newVal[1]
      if (isLoadingStripeCouponsStoreNew) {
        return
      }
      if (promoCodeNew) {
        stripeCoupon.value = await fetchCouponData(promoCodeNew)
      } else {
        stripeCoupon.value = null
      }
      stripeCouponLoading.value = false
    },
    { immediate: true },
  )

  watch(
    authUser,
    (newVal) => {
      if (newVal) {
        if (unsubscribeProductsListener) {
          unsubscribeProductsListener()
        }
        unsubscribeProductsListener = setupProductsListener()
      }
    },
    { immediate: true },
  )

  return {
    products,
    isLoading,
    error,
    activeProducts,
    activeProductsAvailableOnSite,
    getProductById,
    getConsultChargeProduct,
    getConsultTypeProduct,
    fetchCouponData,
    formatPrice,
    productsWithPromoApplied,
    productsAvailableOnSiteWithPromo,
    setupProductsListener,
    reloadProductsCatalog,
    signOut,
    couponAppliesToStripeProduct,
    stripeCouponLoading,
    stripeCoupon,
  }
})
